/**
 * SHOPPING build path — the Product sales objective
 * (src/pages/google-ads/shopping/). Standard Shopping has no creative: every
 * ad is built from the linked Merchant Center feed (title, image, price). A
 * campaign picks the feed; each ad group needs a product group ("All
 * products") and one product ad to serve.
 *
 * Components are MODULE SCOPE on purpose: inline definitions remount on every
 * keystroke and lose focus.
 */
import clsx from 'clsx';
import {
  CheckCircle2, Circle, Copy, Layers, MousePointerClick, Package, ShoppingBag, SlidersHorizontal, Star, Store, TrendingUp, Trash2,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/Input';
import { Card } from '@/components/Card';
import { GoogleAdsCampaignSummary, ShoppingBidding } from '@/services/google-ads.service';
import { Label, Switch, Tag } from '@/components/google-ads/youtube/youtubeShared';
import { MoneyInput, Pub, StepIcon, toMicros, uid } from '@/components/google-ads/objectives/objectiveShared';
import { ChoiceOption, IconBtn, PreviewFrame } from '@/components/google-ads/objectives/wizardChrome';

export interface ShoppingObjectiveConfig {
  title: string; base: string; subtitle: string; icon: LucideIcon; goal: string; emptyHint: string;
}

// ── bidding ──────────────────────────────────────────────────────────────

export const SHOPPING_BIDDING: ReadonlyArray<ChoiceOption<ShoppingBidding>> = [
  { key: 'MANUAL_CPC', title: 'Manual CPC', icon: SlidersHorizontal,
    desc: 'You set the highest price per click on each product group — the safest start for a new feed.', hint: 'Max CPC set per ad group' },
  { key: 'MAXIMIZE_CLICKS', title: 'Maximize clicks', icon: MousePointerClick,
    desc: 'Google finds the most product clicks your budget can buy.', hint: 'Optional max CPC cap' },
  { key: 'TARGET_ROAS', title: 'Target ROAS', icon: TrendingUp, needsConversions: true,
    desc: 'Bids toward a return on ad spend. Needs purchase conversions with values.', hint: 'Target ROAS required' },
];
export const shoppingBiddingTitle = (b: ShoppingBidding) => SHOPPING_BIDDING.find((o) => o.key === b)?.title ?? b;
export const isShoppingCampaign = (c: GoogleAdsCampaignSummary) => c.advertisingChannelType === 'SHOPPING';
export const isManualCpc = (t?: string | null) => (t ?? '').startsWith('MANUAL_CPC');

export const PRIORITIES = [
  { value: '0', label: 'Low', hint: 'The default. Other Shopping campaigns with a higher priority bid first on the same products.' },
  { value: '1', label: 'Medium', hint: 'Wins over low-priority campaigns that advertise the same products.' },
  { value: '2', label: 'High', hint: 'Bids first whenever several of your Shopping campaigns carry the same product — use it for promotions.' },
] as const;
export const priorityLabel = (p?: number | null) => PRIORITIES.find((x) => Number(x.value) === p)?.label ?? 'Low';

// ── drafts ───────────────────────────────────────────────────────────────

export type ShoppingGroupDraft = { id: string; name: string; cpcBid: string; withAd: boolean };
export const newShoppingGroup = (n: number): ShoppingGroupDraft => ({ id: uid(), name: n === 1 ? 'All products' : `Ad group ${n}`, cpcBid: '0.50', withAd: true });
export const cloneShoppingGroup = (g: ShoppingGroupDraft): ShoppingGroupDraft => ({ ...g, id: uid(), name: `${g.name} (copy)` });

export function shoppingGroupIssues(g: ShoppingGroupDraft, manualCpc: boolean): string[] {
  const i: string[] = [];
  if (!g.name.trim()) i.push('a name');
  if (manualCpc && toMicros(g.cpcBid) <= 0) i.push('a max CPC above zero');
  return i;
}
export const shoppingGroupSummary = (g: ShoppingGroupDraft, manualCpc: boolean, currency?: string | null) =>
  ['All products', manualCpc ? `max CPC ${g.cpcBid || '—'}${currency ? ` ${currency}` : ''}` : null, g.withAd ? 'product ad' : 'no ad yet'].filter(Boolean).join(' · ');

// ── editor ───────────────────────────────────────────────────────────────

/** The one product group every new ad group gets, drawn as the root of its tree. */
export function ProductGroupTree({ bid, manualCpc, currency }: { bid?: string; manualCpc: boolean; currency?: string | null }) {
  return (
    <div className="rounded-lg border border-border bg-surface-alt/40 px-3 py-2.5">
      <div className="flex items-center gap-2 text-xs">
        <Package size={13} className="shrink-0 text-primary" />
        <span className="font-medium text-text-primary">All products</span>
        <span className="text-text-muted">— every item in the feed</span>
        <span className="flex-1" />
        {manualCpc && <span className="tabular-nums text-text-secondary">max CPC {bid || '—'} {currency ?? ''}</span>}
      </div>
      <p className="mt-1 pl-5 text-2xs leading-4 text-text-muted">
        Split it later by brand, product type or custom label in Google Ads to bid differently per slice.
      </p>
    </div>
  );
}

export function ShoppingGroupCard({ group: g, index, open, onToggle, manualCpc, currency, state, made, busy, onChange, onDuplicate, onRemove }: {
  group: ShoppingGroupDraft; index: number; open: boolean; onToggle: () => void; manualCpc: boolean; currency?: string | null;
  state?: Pub; made: boolean; busy: boolean; onChange: (p: Partial<ShoppingGroupDraft>) => void; onDuplicate?: () => void; onRemove?: () => void;
}) {
  const issues = shoppingGroupIssues(g, manualCpc);
  const disabled = made || busy;
  return (
    <Card padded={false} className="overflow-hidden">
      <div className={clsx('flex items-center gap-2.5 px-4 py-3', open && 'border-b border-border')}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left" onClick={onToggle} aria-expanded={open}>
          {state && state !== 'idle' ? <StepIcon s={state} /> : <Layers size={15} className="shrink-0 text-text-muted" />}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{g.name.trim() || `Ad group ${index + 1}`}</div>
            <div className="truncate text-2xs text-text-muted">{shoppingGroupSummary(g, manualCpc, currency)}</div>
          </div>
        </button>
        {made ? <Tag tone="ok">created</Tag> : issues.length ? <Tag tone="warn">{issues.length} missing</Tag> : <Tag tone="ok">ready</Tag>}
        {onDuplicate && <IconBtn title="Duplicate ad group" onClick={onDuplicate} disabled={busy}><Copy size={14} /></IconBtn>}
        {onRemove && !made && <IconBtn danger title="Remove ad group" onClick={onRemove} disabled={busy}><Trash2 size={14} /></IconBtn>}
      </div>
      {open && (
        <div className="space-y-3 p-4">
          <div className={manualCpc ? 'grid grid-cols-1 gap-3 sm:grid-cols-[1fr_220px]' : ''}>
            <div>
              <Label>Ad group name</Label>
              <Input className="mt-1" placeholder="e.g. All products" value={g.name} disabled={disabled} onChange={(e) => onChange({ name: e.target.value })} />
            </div>
            {manualCpc && (
              <div>
                <Label>Max CPC</Label>
                <div className="mt-1"><MoneyInput value={g.cpcBid} onChange={(v) => onChange({ cpcBid: v })} currency={currency} disabled={disabled} /></div>
              </div>
            )}
          </div>
          <div>
            <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">Product group</div>
            <ProductGroupTree bid={g.cpcBid} manualCpc={manualCpc} currency={currency} />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-lg bg-surface-alt/60 px-3 py-2.5">
            <div>
              <div className="text-xs font-medium text-text-primary">Create the product ad now</div>
              <p className="mt-0.5 text-2xs leading-4 text-text-muted">Recommended — a Shopping ad group can't show without one. It has no creative; Google fills it from the feed.</p>
            </div>
            <Switch checked={g.withAd} label="Create product ad" disabled={disabled} onCheckedChange={(v) => onChange({ withAd: v })} />
          </div>
        </div>
      )}
    </Card>
  );
}

// ── preview ──────────────────────────────────────────────────────────────

const Tile = ({ tone, local, store, currency }: { tone: string; local: boolean; store: string; currency?: string | null }) => (
  <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-[#dadce0] bg-white dark:border-[#3c4043] dark:bg-[#1f1f1f]">
    <div className={clsx('relative flex aspect-square items-center justify-center', tone)}>
      <ShoppingBag size={22} className="text-white/85" />
      {local && <span className="absolute left-1 top-1 rounded bg-white/90 px-1 text-[8px] font-semibold text-[#188038]">In store</span>}
    </div>
    <div className="space-y-1 p-1.5">
      <div className="h-1.5 w-11/12 rounded bg-[#dadce0] dark:bg-[#3c4043]" />
      <div className="h-1.5 w-2/3 rounded bg-[#dadce0] dark:bg-[#3c4043]" />
      <div className="text-[11px] font-bold text-[#202124] dark:text-[#e8eaed]">{currency || '$'} —</div>
      <div className="truncate text-[9px] text-[#5f6368] dark:text-[#9aa0a6]">{store}</div>
      <div className="flex items-center gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={7} className="fill-[#fbbc04] text-[#fbbc04]" />)}</div>
    </div>
  </div>
);

/**
 * How Shopping ads read on Google — product tiles built from the feed — plus
 * the checklist of what a Standard Shopping campaign needs before it can serve.
 */
export function ShoppingPreview({ merchantId, merchantLinked, groups, withAds, local, feedLabel, currency }: {
  merchantId: string; merchantLinked: boolean | null; groups: number; withAds: number; local: boolean; feedLabel?: string; currency?: string | null;
}) {
  const checks: Array<{ ok: boolean | null; label: string; hint: string }> = [
    { ok: merchantLinked === null ? null : merchantLinked && /^\d{4,}$/.test(merchantId), label: 'Merchant Center linked',
      hint: merchantId ? `Account ${merchantId}${feedLabel ? ` · feed label ${feedLabel}` : ''}` : 'Pick or enter the Merchant Center account' },
    { ok: null, label: 'Products approved in the feed', hint: 'Checked by Google in Merchant Center — disapproved products never show' },
    { ok: groups > 0, label: 'Product group per ad group', hint: `${groups} ad group${groups === 1 ? '' : 's'} · "All products"` },
    { ok: withAds === groups && groups > 0, label: 'A product ad in every group', hint: `${withAds} of ${groups} ad group${groups === 1 ? '' : 's'}` },
  ];
  return (
    <PreviewFrame title="Preview · Google Shopping"
      note="Indicative only — the product, image, title and price come from your Merchant Center feed and change with every search.">
      <div className="rounded-xl border border-border bg-white p-3 text-[#202124] shadow-soft dark:bg-[#1f1f1f] dark:text-[#e8eaed]">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] font-bold">Sponsored</span>
          <span className="text-[10px] text-[#5f6368] dark:text-[#9aa0a6]">Shopping</span>
        </div>
        <div className="flex gap-1.5">
          <Tile tone="bg-gradient-to-br from-[#c8a55b] to-[#9c7e3f]" local={local} store="Your store" currency={currency} />
          <Tile tone="bg-gradient-to-br from-[#5f6368] to-[#3c4043]" local={false} store="Other store" currency={currency} />
          <Tile tone="bg-gradient-to-br from-[#8ab4f8] to-[#1a73e8]" local={false} store="Other store" currency={currency} />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Before it can serve</div>
        {checks.map((c) => (
          <div key={c.label} className="flex items-start gap-2">
            {c.ok === true ? <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-success" />
              : c.ok === false ? <Circle size={13} className="mt-0.5 shrink-0 text-warning" />
              : <Store size={13} className="mt-0.5 shrink-0 text-text-muted" />}
            <div className="min-w-0">
              <div className="text-xs font-medium text-text-primary">{c.label}</div>
              <div className="text-2xs leading-4 text-text-muted">{c.hint}</div>
            </div>
          </div>
        ))}
      </div>
    </PreviewFrame>
  );
}
